/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/no-danger */
// eslint-disable react/no-danger
import windowRoot from 'window-or-global';
import { h } from 'preact';
import { ForwardFn, forwardRef, useCallback } from 'preact/compat';
import {
  useEffect,
  useRef,
  useImperativeHandle,
  useState,
  StateUpdater,
} from 'preact/hooks';
import html2canvas from 'html2canvas';
import { useThrottleFn } from 'ahooks';

import { Box, Dialog } from '@mui/material';

import { IBaseInfo, ISignetInfo } from '../..';
import { uploadPrevive } from '../../../../apis';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../../../utils/http';

interface IProps {
  isWatermarkRemoval: boolean;
  isRealTime: boolean;
  baseInfo: IBaseInfo;
  signetInfo: ISignetInfo;
}

export interface IPreviewHadle {
  downloadImage: (downloadName: string) => void;
  downloadImageCloude: (
    downloadName: string,
    setIsDownloading: StateUpdater<boolean>
  ) => void;
  runPreview: () => void;
}

const Preview: ForwardFn<IProps, IPreviewHadle> = (props, ref) => {
  const { isWatermarkRemoval, isRealTime, baseInfo, signetInfo } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const previewContainerRef = useRef<HTMLImageElement>(null);
  const abrasionContainer = useRef<HTMLImageElement>(null);
  const imgDataRef = useRef<string>('');

  const replaceContentStr = useCallback((str: string) => {
    return str
      .replace(/ /g, '&nbsp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/[\r\n]/g, '<br/>');
  }, []);

  const convertCanvasToImageSrc = (canvas: HTMLCanvasElement) => {
    const data = canvas.toDataURL('image/png');
    imgDataRef.current = data;
    return data;
  };

  const addWatermark = (canvas: HTMLCanvasElement, text: string) => {
    const ctx = canvas.getContext('2d')!;

    const fontSize = canvas.width / 40;

    ctx.font = `${fontSize}px sans-serif`;
    ctx.textBaseline = 'middle'; //设置文本的垂直对齐方式
    ctx.textAlign = 'center'; //设置文本的水平对对齐方式
    ctx.lineWidth = 1.4;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.rotate(6.02);

    const interval = fontSize * 2;
    const sum = text.length * fontSize + interval;

    for (let i = 0; i <= canvas.width + sum; i += sum) {
      for (let j = 0; j <= canvas.height + sum; j += fontSize + interval) {
        ctx.fillText(text, i - interval, j, canvas.width);
      }
    }
  };

  const create5star = (
    context: CanvasRenderingContext2D,
    sx: number,
    sy: number,
    radius: number,
    color: string,
    rotato: number
  ) => {
    context.save();
    context.fillStyle = color;
    context.translate(sx, sy); //移动坐标原点
    context.rotate(Math.PI + rotato); //旋转
    context.beginPath(); //创建路径
    context.scale(sx / 100, sx / 100);
    const dig = (Math.PI / 5) * 4;
    for (let i = 0; i < 5; i++) {
      //画五角星的五条边
      const x = Math.sin(i * dig);
      const y = Math.cos(i * dig);
      context.lineTo(x * radius, y * radius);
    }
    context.closePath();
    context.stroke();
    context.fill();
    context.restore();
  };

  const runDraw = useCallback(() => {
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    if (!canvas) return;
    const context = canvas.getContext('2d')!;
    // eslint-disable-next-line no-self-assign
    canvas.height = canvas.height;

    const text = signetInfo.describe;
    const companyName = signetInfo.content;

    // 绘制印章边框
    const width = canvas.width / 2;
    const height = canvas.height / 2;

    context.lineWidth = (5 * width) / 100;
    context.strokeStyle = signetInfo.borderColor;
    context.beginPath();
    context.arc(width, height, (90 * width) / 100, 0, Math.PI * 2); //宽、高、半径
    context.stroke();

    //画五角星
    create5star(context, width, height, 28, signetInfo.contentColor, 0);

    // 绘制印章描述
    const fontSize = (signetInfo.describeSize * width) / 100;
    context.font = `${fontSize}px ${signetInfo.describeFamily}`;
    context.textBaseline = 'middle'; //设置文本的垂直对齐方式
    context.textAlign = 'center'; //设置文本的水平对对齐方式
    context.lineWidth = (1.4 * width) / 100;
    context.strokeStyle = signetInfo.contentColor;
    context.strokeText(text, width, height + (56 / 176) * signetInfo.size);

    // 绘制印章內容
    const contentFontSize = (signetInfo.contentSize * width) / 100;
    context.translate(width, height); // 平移到此位置,
    context.font = `${contentFontSize}px ${signetInfo.contentFamily}`;
    const count = companyName.length; // 字数
    const angle = (4 * Math.PI) / (3 * (count - 1)); // 字间角度
    const chars = companyName.split('');
    let c;
    for (let i = 0; i < count; i++) {
      c = chars[i]; // 需要绘制的字符
      if (i == 0) {
        context.rotate((5 * Math.PI) / 6);
      } else {
        context.rotate(angle);
      }
      context.save();
      context.translate((70 * width) / 100, 0); // 平移到此位置,此时字和x轴垂直，公司名称和最外圈的距离
      context.rotate(Math.PI / 2); // 旋转90度,让字平行于x轴
      context.strokeText(c, 0, 0); // 此点为字的中心点
      context.restore();
    }
  }, [signetInfo]);

  const drawPreview = useCallback(() => {
    if (!containerRef.current) return;
    if (!previewContainerRef.current) return;

    runDraw();

    html2canvas(containerRef.current, {
      scale: 2, //按比例增加分辨率
    }).then((canvas) => {
      const targetCanvas = document.createElement('canvas');
      const ctx = targetCanvas.getContext('2d')!;

      targetCanvas.width = canvas.width;
      targetCanvas.height = canvas.height;

      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);

      !isWatermarkRemoval && addWatermark(targetCanvas, '开通会员自动去水印');

      previewContainerRef.current?.setAttribute(
        'src',
        convertCanvasToImageSrc(targetCanvas)
      );
    });
  }, [isWatermarkRemoval, runDraw]);

  const { run: runPreview } = useThrottleFn(drawPreview, { wait: 200 });

  const base64ImgtoFile = (dataurl: string, filename = 'file') => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const suffix = mime.split('/')[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], `${filename}.${suffix}`, { type: mime });
  };

  const downloadImageCloude = async (
    downloadName: string,
    setIsDownloading: StateUpdater<boolean>
  ) => {
    const formData = new FormData();
    formData.append('image', base64ImgtoFile(imgDataRef.current, downloadName));
    formData.append('name', downloadName);

    setIsDownloading(true);

    const { success, data } = await uploadPrevive(formData);

    if (!success) {
      toast('下载失败！', { type: 'error' });
      setIsDownloading(false);
      return;
    }

    const imgUrl = `${BASE_URL}/${data.path}`;

    toast('请长按图片保存', { type: 'info' });
    setPreviewUrl(imgUrl);
    setIsDownloading(false);
  };

  const downloadImage = (downloadName: string) => {
    const blob = base64ImgtoFile(imgDataRef.current, downloadName);
    const blobUrl = windowRoot.URL.createObjectURL(blob);
    const filename = blob.name;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.click();
    windowRoot.URL.revokeObjectURL(blobUrl);
  };

  useImperativeHandle(ref, () => ({
    downloadImage,
    downloadImageCloude,
    runPreview,
  }));

  useEffect(() => {
    isRealTime && runPreview();
  }, [isRealTime, baseInfo, signetInfo, runPreview]);

  useEffect(() => {
    runPreview();
  }, [isWatermarkRemoval, runPreview]);

  useEffect(() => {
    if (abrasionContainer.current) {
      abrasionContainer.current.onload = () => {
        windowRoot.setTimeout(() => {
          runPreview();
        }, 200);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abrasionContainer.current]);

  useEffect(() => {
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box style={{ height: '100%' }}>
      <img
        ref={previewContainerRef}
        title="展示"
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />

      <Box
        ref={containerRef}
        id="container"
        style={{
          position: 'fixed',
          top: '-999999px',
          margin: '0 auto',
          width: `${baseInfo.width}px`,
          height: `${baseInfo.height}px`,
          backgroundColor: baseInfo.backgroundColor,
          padding: `0 ${baseInfo.padding}px`,
        }}
      >
        <Box style={{ marginTop: `${baseInfo.marginTop}px` }}>
          <Box>
            <p
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: `${baseInfo.titleFontSize}px`,
                marginBottom: `${baseInfo.titleMarginBottom}px`,
                fontFamily: baseInfo.titleFontFamily,
              }}
              dangerouslySetInnerHTML={{
                __html: replaceContentStr(baseInfo.title),
              }}
            />

            <p
              style={{
                fontFamily: baseInfo.contentFontFamily,
                fontSize: `${baseInfo.contentFontSize}px`,
                lineHeight: `${baseInfo.contentLineHeight}px`,
                color: baseInfo.contentFontColor,
                fontWeight: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
              dangerouslySetInnerHTML={{
                __html: replaceContentStr(baseInfo.content),
              }}
            />

            <Box
              style={{
                display: 'inline-block',
                textAlign: 'center',
                position: 'absolute',
                right: `${baseInfo.rbRight}px`,
                bottom: `${baseInfo.rbBottom}px`,
                lineHeight: `${baseInfo.rbContentLineHeight}px`,
                fontSize: `${baseInfo.rbContentFontSize}px`,
                fontFamily: baseInfo.rbContentFontFamily,
                color: baseInfo.rbContentFontColor,
              }}
            >
              <p
                style="margin: 0;"
                dangerouslySetInnerHTML={{
                  __html: replaceContentStr(baseInfo.rbContent1),
                }}
              />
              <p
                style="margin: 0"
                dangerouslySetInnerHTML={{
                  __html: replaceContentStr(baseInfo.rbContent2),
                }}
              />
            </Box>
          </Box>
        </Box>
        <div
          style={{
            opacity: signetInfo.opacity / 100,
            position: 'absolute',
            right: `${signetInfo.offsetX}px`,
            bottom: `${signetInfo.offsetY}px`,
            transform: `rotate(${signetInfo.rotate}deg)`,
            width: `${signetInfo.size}px`,
            height: `${signetInfo.size}px`,
            overflow: 'hidden',
          }}
        >
          <canvas
            id="canvas"
            width={signetInfo.size}
            height={signetInfo.size}
            style="position: absolute;top: 0; left: 0; width: 100%; height: 100%;"
          />
          <img
            src="/assets/images/abrasion.png"
            alt=""
            id="abrasionContainer"
            ref={abrasionContainer}
            style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              opacity: signetInfo.abrasion / 100,
            }}
          />
        </div>
      </Box>

      <Dialog
        open={!!previewUrl}
        onClose={() => setPreviewUrl('')}
        PaperProps={{ sx: { margin: '16px' } }}
      >
        <div
          onClick={() => setPreviewUrl('')}
          style={{
            background: '#3898ff',
            padding: '0.5rem',
            textAlign: 'center',
            color: 'white',
            fontSize: '0.9rem',
            fontWeight: 'bold',
          }}
        >
          请长按图片保存
        </div>

        <img src={previewUrl} />
      </Dialog>
    </Box>
  );
};

export default forwardRef(Preview);
